import { useState } from 'react';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { useGetCubeDataEntryReferenceAudit } from 'services/v1/Tenant/AROCube/CubeDataEntryService';
import {
  CubeAuditMismatchType,
  CubeReferenceAuditMismatch,
  ResponseCubeDataEntryReferenceAudit,
} from 'types/api/Tenant/AROCube/CubeDataEntryTypes';
import { getErrorMessage } from 'utils/Error';

interface ModalDeveloperToolsReferenceAuditProps {
  open: boolean;
  onClose: () => void;
  cubeId: string | undefined;
  memberId: string | undefined;
  layerId: string | undefined;
  xMemberId: string | undefined;
  timeIntervalId: string | undefined;
  layerName?: string;
  memberName?: string;
}

const modalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90vw',
  maxWidth: 1100,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
  outline: 'none',
  display: 'flex',
  flexDirection: 'column' as const,
};

const MISMATCH_TYPE_COLOR: Record<string, { bg: string; fg: string }> = {
  VALUE_MISMATCH: { bg: '#FDECEA', fg: '#B71C1C' },
  MISSING_PHYSICAL_ROW: { bg: '#FFF4E5', fg: '#8A5300' },
  INVALID_PHYSICAL_ROW: { bg: '#EDE7F6', fg: '#4A148C' },
};

function getMismatchColor(type: CubeAuditMismatchType) {
  return MISMATCH_TYPE_COLOR[type] || { bg: '#ECEFF1', fg: '#37474F' };
}

function formatValue(value: number | string | null) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return String(value);
  return value;
}

function SummaryStat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Tooltip title={hint || ''} arrow placement='top' disableHoverListener={!hint}>
      <Box
        sx={{
          flex: 1,
          minWidth: 110,
          px: 1.5,
          py: 1,
          border: '1px solid #E3EBF6',
          borderRadius: 1,
          bgcolor: '#FAFBFD',
        }}
      >
        <Typography variant='caption' sx={{ color: '#888', display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10 }}>
          {label}
        </Typography>
        <Typography variant='body1' sx={{ color: '#3B4797', fontWeight: 600, mt: 0.25 }}>
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function HealthBadge({ healthy }: { healthy: boolean }) {
  return (
    <Chip
      size='small'
      icon={healthy ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
      label={healthy ? 'Healthy' : 'Unhealthy'}
      sx={{
        bgcolor: healthy ? '#E6F4EA' : '#FDECEA',
        color: healthy ? '#1B5E20' : '#B71C1C',
        fontWeight: 600,
        '& .MuiChip-icon': { color: healthy ? '#1B5E20' : '#B71C1C' },
      }}
    />
  );
}

function parsePath(path: string): { timePeriod: string; attribute: string } | null {
  const match = path.match(/^data\[([^\]]+)\]\[([^\]]+)\]$/);
  if (!match) return null;
  return { timePeriod: match[1], attribute: match[2] };
}

function toNumber(value: number | string | null): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function computeDelta(local: number | string | null, reference: number | string | null) {
  const l = toNumber(local);
  const r = toNumber(reference);
  if (l === null || r === null) return { delta: null as number | null, pct: null as number | null };
  const delta = l - r;
  const pct = r === 0 ? null : (delta / r) * 100;
  return { delta, pct };
}

function formatDelta(delta: number | null) {
  if (delta === null) return '—';
  const sign = delta > 0 ? '+' : '';
  const abs = Math.abs(delta);
  const formatted = abs >= 1 ? delta.toFixed(2) : delta.toPrecision(4);
  return `${sign}${formatted}`;
}

function formatPct(pct: number | null) {
  if (pct === null) return '';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

function deltaColor(delta: number | null) {
  if (delta === null || delta === 0) return '#666';
  return delta > 0 ? '#B71C1C' : '#1B5E20';
}

function MismatchTable({ mismatches }: { mismatches: CubeReferenceAuditMismatch[] }) {
  if (mismatches.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#FAFBFD', borderRadius: 1, border: '1px dashed #E3EBF6' }}>
        <Typography variant='body2' color='text.secondary'>
          No mismatches reported.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
      <Box sx={{ overflow: 'auto', maxHeight: '40vh' }}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }}>Time Period</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }}>Attribute</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }}>Path</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }} align='right'>Local</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }} align='right'>Reference</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }} align='right'>Delta</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mismatches.map((m, idx) => {
              const color = getMismatchColor(m.type);
              const parts = parsePath(m.path);
              const { delta, pct } = computeDelta(m.localValue, m.referenceValue);
              const dColor = deltaColor(delta);
              return (
                <TableRow key={`${m.type}-${m.path}-${idx}`} hover>
                  <TableCell>
                    <Chip
                      size='small'
                      label={m.type}
                      sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 600, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell>{parts?.timePeriod ?? '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{parts?.attribute ?? '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: '#666' }}>{m.path}</TableCell>
                  <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatValue(m.localValue)}</TableCell>
                  <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatValue(m.referenceValue)}</TableCell>
                  <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: 12, color: dColor, fontWeight: 600 }}>
                    {formatDelta(delta)}
                    {pct !== null && (
                      <Typography component='span' sx={{ ml: 0.5, fontSize: 10, color: dColor, opacity: 0.75 }}>
                        ({formatPct(pct)})
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}

function RawJsonView({ data }: { data: ResponseCubeDataEntryReferenceAudit }) {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore clipboard failures
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title={copied ? 'Copied!' : 'Copy JSON'} arrow placement='left'>
        <IconButton
          size='small'
          onClick={handleCopy}
          sx={{ position: 'absolute', top: 6, right: 6, bgcolor: '#fff', '&:hover': { bgcolor: '#f0f0f0' } }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Box
        component='pre'
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: '#0F172A',
          color: '#E2E8F0',
          fontSize: 11,
          fontFamily: 'monospace',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: '30vh',
        }}
      >
        {json}
      </Box>
    </Box>
  );
}

export function ModalDeveloperToolsReferenceAudit({
  open,
  onClose,
  cubeId,
  memberId,
  layerId,
  xMemberId,
  timeIntervalId,
  layerName,
  memberName,
}: ModalDeveloperToolsReferenceAuditProps) {
  const [showRaw, setShowRaw] = useState(false);

  const {
    data: auditResponse,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetCubeDataEntryReferenceAudit(
    cubeId,
    memberId,
    layerId,
    xMemberId,
    timeIntervalId,
    { enabled: open }
  );

  const audit = auditResponse?.data;
  const ready = !!cubeId && !!layerId && !!memberId && !!timeIntervalId;

  const handleClose = () => {
    setShowRaw(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={modalContentStyle} spacing={2}>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Typography variant='body1' fontWeight='bold' sx={{ color: '#3B4797' }}>
              Developer Tools — Reference Audit
            </Typography>
            {audit && <HealthBadge healthy={audit.healthy} />}
          </Stack>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Tooltip title='Refresh' arrow>
              <span>
                <IconButton size='small' onClick={() => refetch()} disabled={isFetching || !ready}>
                  <RefreshIcon sx={{ color: '#3B4797' }} />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size='small' onClick={handleClose}>
              <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
          <Typography variant='caption' color='text.secondary'>
            <b>Cube:</b> <span style={{ fontFamily: 'monospace' }}>{cubeId ?? '—'}</span>
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            <b>Layer:</b>{' '}
            <span style={{ fontFamily: 'monospace' }}>
              {layerName ? `${layerName} (${layerId ?? '—'})` : (layerId ?? '—')}
            </span>
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            <b>Member:</b>{' '}
            <span style={{ fontFamily: 'monospace' }}>
              {memberName ? `${memberName} (${memberId ?? '—'})` : (memberId ?? '—')}
            </span>
          </Typography>
          {xMemberId && (
            <Typography variant='caption' color='text.secondary'>
              <b>X-Member:</b> <span style={{ fontFamily: 'monospace' }}>{xMemberId}</span>
            </Typography>
          )}
          {timeIntervalId && (
            <Typography variant='caption' color='text.secondary'>
              <b>Time Interval:</b> <span style={{ fontFamily: 'monospace' }}>{timeIntervalId}</span>
            </Typography>
          )}
        </Stack>

        {audit && (audit.localPath || audit.referenceUrl) && (
          <Stack spacing={0.5} sx={{ p: 1, bgcolor: '#FAFBFD', borderRadius: 1, border: '1px solid #E3EBF6' }}>
            {audit.localPath && (
              <Typography variant='caption' color='text.secondary' sx={{ wordBreak: 'break-all' }}>
                <b>Local:</b>{' '}
                <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{audit.localPath}</span>
              </Typography>
            )}
            {audit.referenceUrl && (
              <Typography variant='caption' color='text.secondary' sx={{ wordBreak: 'break-all' }}>
                <b>Reference:</b>{' '}
                <a
                  href={audit.referenceUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ fontFamily: 'monospace', fontSize: 11, color: '#3B4797' }}
                >
                  {audit.referenceUrl}
                </a>
              </Typography>
            )}
          </Stack>
        )}

        <Divider />

        <Box sx={{ flex: 1, overflow: 'auto', pr: 0.5 }}>
          {!ready ? (
            <Alert severity='warning'>
              Select a cube, layer, member, and time interval to run the reference audit.
            </Alert>
          ) : isFetching && !audit ? (
            <Stack alignItems='center' justifyContent='center' sx={{ py: 4 }}>
              <ActivityIndicator />
              <Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
                Running reference audit…
              </Typography>
            </Stack>
          ) : isError ? (
            <Alert severity='error'>{getErrorMessage(error)}</Alert>
          ) : audit ? (
            <Stack spacing={2}>
              <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                <SummaryStat label='Checked Cells' value={audit.checkedCellCount} hint='Individual cells compared between local and reference' />
                <SummaryStat label='Mismatches' value={audit.mismatchCount} hint='Total mismatches detected' />
                <SummaryStat label='Tolerance' value={audit.tolerance} hint='Numeric tolerance applied when comparing values' />
                <SummaryStat label='Limit' value={audit.limit} hint='Maximum mismatches returned (truncated if reached)' />
              </Stack>

              {audit.truncated && (
                <Alert severity='info'>
                  Results were truncated at {audit.limit} mismatches — there may be more issues not shown below.
                </Alert>
              )}

              <Stack direction='row' alignItems='center' justifyContent='space-between'>
                <Typography variant='body2' fontWeight={600} sx={{ color: '#3B4797' }}>
                  Mismatches ({audit.mismatches.length})
                </Typography>
                <Button
                  size='small'
                  variant='text'
                  onClick={() => setShowRaw((v) => !v)}
                  sx={{ textTransform: 'none' }}
                >
                  {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
                </Button>
              </Stack>

              <MismatchTable mismatches={audit.mismatches} />

              {showRaw && <RawJsonView data={audit} />}
            </Stack>
          ) : null}
        </Box>

        <Divider />
        <Stack direction='row' spacing={1.5} justifyContent='flex-end'>
          <Button
            variant='main-table-panel-border'
            onClick={handleClose}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
